
import React, { createContext, useContext, useReducer, useEffect, ReactNode } from 'react';
import { useOptimizedAuth } from '@/hooks/useOptimizedAuth';
import { supabase } from '@/integrations/supabase/client';

export interface CartItem {
  id: string;
  product_id: string;
  quantity: number;
  price: number;
  name: string;
  image_url?: string;
  stock_quantity?: number;
  variant_id?: string;
  variant_name?: string;
}

export interface CartState {
  items: CartItem[];
  total: number;
  isLoading: boolean;
}

interface CartAction {
  type: 'SET_LOADING' | 'SET_ITEMS' | 'ADD_ITEM' | 'UPDATE_ITEM' | 'REMOVE_ITEM' | 'CLEAR_CART';
  payload?: any;
}

export interface OptimizedCartContextType {
  state: CartState;
  items: CartItem[];
  itemCount: number;
  total: number;
  addItem: (item: Omit<CartItem, 'id'>) => void;
  addToCart: (product: any, quantity?: number) => void;
  updateItem: (id: string, quantity: number) => void;
  updateQuantity: (id: string, quantity: number) => void;
  removeItem: (id: string) => void;
  removeFromCart: (id: string) => void;
  clearCart: () => void;
}

const initialState: CartState = {
  items: [],
  total: 0,
  isLoading: false,
};

function cartReducer(state: CartState, action: CartAction): CartState {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };
    case 'SET_ITEMS':
      const items = action.payload || [];
      return {
        ...state,
        items,
        total: items.reduce((sum: number, item: CartItem) => sum + (item.price * item.quantity), 0),
        isLoading: false,
      };
    case 'ADD_ITEM':
      const newItem = { ...action.payload, id: Date.now().toString() };
      const updatedItems = [...state.items, newItem];
      return {
        ...state,
        items: updatedItems,
        total: updatedItems.reduce((sum, item) => sum + (item.price * item.quantity), 0),
      };
    case 'UPDATE_ITEM':
      const itemsAfterUpdate = state.items.map(item =>
        item.id === action.payload.id
          ? { ...item, quantity: action.payload.quantity }
          : item
      );
      return {
        ...state,
        items: itemsAfterUpdate,
        total: itemsAfterUpdate.reduce((sum, item) => sum + (item.price * item.quantity), 0),
      };
    case 'REMOVE_ITEM':
      const itemsAfterRemoval = state.items.filter(item => item.id !== action.payload);
      return {
        ...state,
        items: itemsAfterRemoval,
        total: itemsAfterRemoval.reduce((sum, item) => sum + (item.price * item.quantity), 0),
      };
    case 'CLEAR_CART':
      return initialState;
    default:
      return state;
  }
}

export const OptimizedCartContext = createContext<OptimizedCartContextType | undefined>(undefined);

export const OptimizedCartProvider = ({ children }: { children: ReactNode }) => {
  const [state, dispatch] = useReducer(cartReducer, initialState);
  const { user } = useOptimizedAuth();

  const addItem = (item: Omit<CartItem, 'id'>) => {
    dispatch({ type: 'ADD_ITEM', payload: item });
  };

  const updateItem = (id: string, quantity: number) => {
    dispatch({ type: 'UPDATE_ITEM', payload: { id, quantity } });
  };

  const removeItem = (id: string) => {
    dispatch({ type: 'REMOVE_ITEM', payload: id });
  };

  const clearCart = () => {
    dispatch({ type: 'CLEAR_CART' });
  };

  // Load cart items for authenticated users
  useEffect(() => {
    const loadCart = async () => {
      dispatch({ type: 'SET_LOADING', payload: true });
      
      if (user) {
        try {
          // Load cart from database for authenticated users
          const { data: cartItems, error } = await supabase
            .from('shopping_cart')
            .select('id, product_id, quantity, created_at')
            .eq('user_id', user.id);

          if (error) {
            console.error('Error loading cart from database:', error);
            // Fallback to localStorage
            const savedCart = localStorage.getItem('cart');
            if (savedCart) {
              const items = JSON.parse(savedCart);
              dispatch({ type: 'SET_ITEMS', payload: items });
            } else {
              dispatch({ type: 'SET_LOADING', payload: false });
            }
            return;
          }

          // Load product details for each cart item
          const transformedItems: CartItem[] = [];
          for (const cartItem of cartItems || []) {
            const { data: product } = await supabase
              .from('products')
              .select('id, name, price, image_url, stock_quantity')
              .eq('id', cartItem.product_id)
              .single();

            if (product) {
              transformedItems.push({
                id: cartItem.id,
                product_id: cartItem.product_id,
                quantity: cartItem.quantity,
                price: product.price,
                name: product.name,
                image_url: product.image_url,
                stock_quantity: product.stock_quantity,
              });
            }
          }

          dispatch({ type: 'SET_ITEMS', payload: transformedItems });
        } catch (error) {
          console.error('Error loading cart:', error);
          dispatch({ type: 'SET_LOADING', payload: false });
        }
      } else {
        // Load from localStorage for guests
        const savedCart = localStorage.getItem('cart');
        if (savedCart) {
          try {
            const items = JSON.parse(savedCart);
            dispatch({ type: 'SET_ITEMS', payload: items });
          } catch (error) {
            console.error('Error loading cart from localStorage:', error);
            dispatch({ type: 'SET_LOADING', payload: false });
          }
        } else {
          dispatch({ type: 'SET_LOADING', payload: false });
        }
      }
    };

    loadCart();
  }, [user]);

  // Save cart to localStorage and database when items change
  useEffect(() => {
    const saveCart = async () => {
      if (user && state.items.length >= 0) {
        try {
          // Save to database for authenticated users
          // First, clear existing cart items for this user
          await supabase
            .from('shopping_cart')
            .delete()
            .eq('user_id', user.id);

          // Then insert new cart items
          if (state.items.length > 0) {
            const cartItemsToSave = state.items.map(item => ({
              user_id: user.id,
              product_id: item.product_id,
              quantity: item.quantity,
            }));

            await supabase
              .from('shopping_cart')
              .insert(cartItemsToSave);
          }
        } catch (error) {
          console.error('Error saving cart to database:', error);
        }
      }

      // Always save to localStorage as backup
      if (state.items.length > 0) {
        localStorage.setItem('cart', JSON.stringify(state.items));
      } else {
        localStorage.removeItem('cart');
      }
    };

    // Only save if we're not in the initial loading state
    if (!state.isLoading) {
      saveCart();
    }
  }, [state.items, user, state.isLoading]);

  // Helper method to convert product to cart item
  const addToCart = (product: any, quantity: number = 1) => {
    // Check if item already exists (consider variant_id for uniqueness)
    const existingItem = state.items.find(item => 
      item.product_id === product.id && 
      item.variant_id === product.variant_id
    );
    
    if (existingItem) {
      // Update quantity of existing item
      updateItem(existingItem.id, existingItem.quantity + quantity);
    } else {
      // Add new item
      const cartItem: Omit<CartItem, 'id'> = {
        product_id: product.id,
        quantity,
        price: product.price,
        name: product.name,
        image_url: product.image_url,
        stock_quantity: product.stock_quantity,
        variant_id: product.variant_id,
        variant_name: product.variant_name,
      };
      addItem(cartItem);
    }
  };

  // Alias methods for compatibility
  const updateQuantity = updateItem;
  const removeFromCart = removeItem;

  const value: OptimizedCartContextType = {
    state,
    items: state.items,
    itemCount: state.items.length,
    total: state.total,
    addItem,
    addToCart,
    updateItem,
    updateQuantity,
    removeItem,
    removeFromCart,
    clearCart,
  };

  return (
    <OptimizedCartContext.Provider value={value}>
      {children}
    </OptimizedCartContext.Provider>
  );
};

export const useOptimizedCart = () => {
  const context = useContext(OptimizedCartContext);
  if (context === undefined) {
    throw new Error('useOptimizedCart must be used within an OptimizedCartProvider');
  }
  return context;
};
